import { motion } from 'framer-motion';
import { useQuery } from '@tanstack/react-query';
import { 
  Droplets, 
  AlertTriangle, 
  CheckCircle2, 
  Clock,
  Zap,
  Radio
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { cn } from '@/lib/utils';
import { dashboardService } from '@/services';
import { useAuth } from '@/contexts/AuthContext';
import type { ApiActivityEvent } from '@/types/api';

const activityIcons: Record<string, { icon: typeof Droplets; color: string }> = {
  irrigation_start: { icon: Droplets, color: 'text-accent bg-accent/10' },
  irrigation_end: { icon: CheckCircle2, color: 'text-success bg-success/10' },
  trigger_activated: { icon: Zap, color: 'text-warning bg-warning/10' },
  device_offline: { icon: AlertTriangle, color: 'text-destructive bg-destructive/10' },
  sensor_reading: { icon: Radio, color: 'text-info bg-info/10' },
  // Tipos adicionales que pueden venir del backend
  irrigation_event: { icon: Droplets, color: 'text-accent bg-accent/10' },
  schedule_triggered: { icon: Zap, color: 'text-warning bg-warning/10' },
  trigger_fired: { icon: Zap, color: 'text-warning bg-warning/10' },
};

function formatTimeAgo(timestamp: string): string {
  const date = new Date(timestamp);
  const seconds = Math.floor((Date.now() - date.getTime()) / 1000);
  
  if (seconds < 60) return 'Ahora mismo';
  if (seconds < 3600) return `Hace ${Math.floor(seconds / 60)} min`;
  if (seconds < 86400) return `Hace ${Math.floor(seconds / 3600)} h`;
  return `Hace ${Math.floor(seconds / 86400)} días`;
}

function getActivityIcon(type: string) {
  // Normalizar el tipo para mapear correctamente
  const normalizedType = type.toLowerCase().replace(/[_-]/g, '_');
  return activityIcons[normalizedType] || activityIcons.sensor_reading;
}

interface RecentActivityProps {
  className?: string;
}

export function RecentActivity({ className }: RecentActivityProps) {
  const { currentUser } = useAuth();
  // Obtener companyId si el usuario es company_admin (el backend filtra automáticamente)
  const companyId = currentUser?.role === 'company_admin' ? currentUser.companyId : undefined;
  
  const { data: activityData, isLoading, error } = useQuery({
    queryKey: ['dashboard-activity', companyId],
    queryFn: async () => {
      // El backend filtra automáticamente según el usuario autenticado
      const response = await dashboardService.getActivity(10);
      if (!response.success) {
        throw new Error(response.error?.message || 'Error al cargar actividad');
      }
      return response.data;
    },
    refetchInterval: 30000, // Refresh every 30 seconds
  });

  const activities: ApiActivityEvent[] = activityData?.events || [];

  if (isLoading) {
    return (
      <Card className={className}>
        <CardHeader className="pb-2">
          <CardTitle className="text-lg font-display flex items-center gap-2">
            <Clock className="w-5 h-5 text-muted-foreground" />
            Actividad Reciente
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="flex items-start gap-3 p-2">
                <Skeleton className="w-8 h-8 rounded-lg" />
                <div className="flex-1 space-y-2">
                  <Skeleton className="h-4 w-full" />
                  <Skeleton className="h-3 w-24" />
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className={className}>
        <CardHeader className="pb-2">
          <CardTitle className="text-lg font-display flex items-center gap-2">
            <Clock className="w-5 h-5 text-muted-foreground" />
            Actividad Reciente
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Alert variant="destructive">
            <AlertDescription>
              {error instanceof Error ? error.message : 'Error al cargar actividad reciente'}
            </AlertDescription>
          </Alert>
        </CardContent>
      </Card>
    );
  }

  if (activities.length === 0) {
    return (
      <Card className={className}>
        <CardHeader className="pb-2">
          <CardTitle className="text-lg font-display flex items-center gap-2">
            <Clock className="w-5 h-5 text-muted-foreground" />
            Actividad Reciente
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground text-center py-4">
            No hay actividad reciente
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={className}>
      <CardHeader className="pb-2">
        <CardTitle className="text-lg font-display flex items-center gap-2">
          <Clock className="w-5 h-5 text-muted-foreground" />
          Actividad Reciente
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          {activities.map((activity, index) => {
            const { icon: Icon, color } = getActivityIcon(activity.type);
            
            return (
              <motion.div
                key={activity.id}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.05 }}
                className="flex items-start gap-3 p-2 rounded-lg hover:bg-muted/50 transition-colors"
              >
                <div className={cn('p-2 rounded-lg', color)}>
                  <Icon className="w-4 h-4" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-foreground truncate">
                    {activity.message}
                    {activity.gardenName && (
                      <span className="text-muted-foreground ml-1">
                        ({activity.gardenName})
                      </span>
                    )}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {formatTimeAgo(activity.timestamp)}
                  </p>
                </div>
              </motion.div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}
